import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { CSVLink } from "react-csv";
import "react-csv-importer/dist/index.css";
import { useLocationStore, usePipedriveStore } from "../../index";
import { uploadedLocationStatusToString } from "../../utils/enumUtils";
import { formatDate, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import DownloadIcon from "../icons/DownloadIcon";
import addLocationsStyles from "./AddLocations.module.css";
import "./Importer.css";
import { IUploadedLocationInfo, UploadedLocationStatus } from "./UploadMultipleLocations";

function MultipleLocationsDetails({ companyId, locationsInfo, onImportCanceled, onAdd }: IMultipleLocationsDetailsProps) {
    const locationStore = useLocationStore();
    const pipedriveStore = usePipedriveStore();
    const company = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === companyId)), [companyId]).get()!;
    const isAllCanBeAdded = useMemo(() => locationsInfo.every(li => li.status === UploadedLocationStatus.CanBeAdded), [locationsInfo]);
    const invalidLocationsData = useMemo(() => [
        ["№", "Site Name", "Address", "Lat", "Lon", "Status"],
        ...locationsInfo.filter(li => li.status !== UploadedLocationStatus.CanBeAdded).map(li => [
            li.serialNumber.toString(),
            li.siteName,
            li.address,
            li.lat,
            li.lon,
            uploadedLocationStatusToString(li.status),
        ]),
    ], [locationsInfo]);

    const addLocations = async () => {
        const locationsToAdd = locationsInfo
            .filter(l => l.status === UploadedLocationStatus.CanBeAdded)
            .map(l => ({
                pipedriveCompanyId: companyId,
                regridId: l.property?.regridId ?? null,
                siteName: l.siteName,
                address: l.address,
                notes: l.notes,
                property: l.property!,
            }));

        await locationStore.addLocations(locationsToAdd);
    }

    return (
        <div>
            <div className={addLocationsStyles.uploadedLocationsBody}>
                <div className={addLocationsStyles.locationsTableTitle}>
                    {isAllCanBeAdded ? (
                        "Locations to add"
                    ) : (
                        <>
                            Warning: Some locations cannot be added
                            <CSVLink data={invalidLocationsData} filename={`${company.name} - Invalid locations.csv`}>
                                <button className={addLocationsStyles.control}>
                                    <DownloadIcon color="#6B6B6B" /> Download
                                </button>
                            </CSVLink>
                        </>
                    )}
                </div>
                <div className={addLocationsStyles.verticalScroll}>
                    <table className={addLocationsStyles.locationsTable}>
                        <thead>
                            {isAllCanBeAdded ? (
                                <tr>
                                    <th>№</th>
                                    <th>Site Name</th>
                                    <th>Address</th>
                                    <th>Zoning</th>
                                    <th>Building Size</th>
                                    <th>Land Size</th>
                                    <th>Owner Name</th>
                                    <th>Date Sold</th>
                                    <th>Sale Price</th>
                                    <th>Owner Address</th>
                                    <th>Notes</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th>№</th>
                                    <th>Site Name</th>
                                    <th>Address</th>
                                    <th>Lat</th>
                                    <th>Lon</th>
                                    <th>Status</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {isAllCanBeAdded ? (
                                locationsInfo
                                    .filter(li => li.status === UploadedLocationStatus.CanBeAdded)
                                    .map((location, index) => (
                                        <tr key={index}>
                                            <td>{location.serialNumber}</td>
                                            <td>{location.siteName}</td>
                                            <td>{location.address}</td>
                                            <td>{location.property!.zoning}</td>
                                            <td>{!!location.property!.buildingSize && formatNumber(location.property!.buildingSize)}</td>
                                            <td>{!!location.property!.landSize && formatNumber(location.property!.landSize)}</td>
                                            <td>{location.property!.ownerEntity}</td>
                                            <td>{!!location.property!.saleDate && formatDate(new Date(location.property!.saleDate))}</td>
                                            <td>{!!location.property!.salePrice && formatPrice(location.property!.salePrice)}</td>
                                            <td>{location.property!.ownerAddress}</td>
                                            <td>{location.notes}</td>
                                        </tr>
                                    ))
                            ) : (
                                locationsInfo
                                    .filter(li => li.status !== UploadedLocationStatus.CanBeAdded)
                                    .map((location, index) => (
                                        <tr key={index} className={addLocationsStyles.statusRow}>
                                            <td>{location.serialNumber}</td>
                                            <td>{location.siteName}</td>
                                            <td>{location.address}</td>
                                            <td>{location.lat}</td>
                                            <td>{location.lon}</td>
                                            <td colSpan={8}>{uploadedLocationStatusToString(location.status)}</td>
                                        </tr>
                                    ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className={[commonStyles.modalControls, commonStyles.controlGroup].join(" ")}>
                <button className={commonStyles.secondaryButton} onClick={onImportCanceled}>Previous</button>
                <button className={commonStyles.primaryButton} onClick={() => {
                    addLocations();
                    onAdd();
                }}>Add</button>
            </div>
        </div>
    );
}

interface IMultipleLocationsDetailsProps {
    companyId: number;
    locationsInfo: IUploadedLocationInfo[];
    onImportCanceled: () => void;
    onAdd: () => void;
}

export default observer(MultipleLocationsDetails);